/**
 * Purchase orders by vendor — `purchasing-purchase-orders.png`.
 *
 * Grouped by vendor because that is how they are collected: one trip to
 * SiteOne picks up three orders, and the counter opens at 6:30 whatever else
 * is happening. The vendor's terms and hours ride on the group header for the
 * same reason.
 *
 * No prices. The PO carries quantities; the vendor's price lands with the
 * receipt, and verifying that receipt is what values the stock (§A.1).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type { MeResponse } from '@rowted/shared'
import { PageBar } from '../frame/page-bar.js'
import {
  Avatar, Button, Card, EmptyState, MetricCard, SectionLabel, Tag, type TagTone,
} from '../components/primitives.js'
import { Toast } from '../components/overlays.js'
import { formatRate, formatRelative, parseDateOnly } from '../lib/format.js'
import { request } from '../lib/api.js'
import { cn } from '../lib/cn.js'
import type {
  PurchaseOrderLine, PurchaseOrderResponse, PurchaseOrderRow,
} from '../lib/purchasing-types.js'
import { buyingCount, buyingUnitWord, poLineWords } from '@rowted/shared'

const TABS = [
  { key: 'open', label: 'Open' },
  { key: 'pickups_today', label: 'Pickups today' },
  { key: 'awaiting_receipt', label: 'Awaiting receipt' },
  { key: 'ready_to_sync', label: 'Ready to sync' },
  { key: 'all', label: 'All' },
] as const

const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  placed: 'Placed',
  ready_for_pickup: 'Ready for pickup',
  picked_up: 'Picked up',
  delivered: 'Delivered',
  receipt_verified: 'Verified',
  synced: 'Synced to QuickBooks',
  cancelled: 'Cancelled',
}

/** What each status can become, so the panel offers the next step and no other. */
const NEXT: Record<string, { to: string; label: string }[]> = {
  draft: [{ to: 'placed', label: 'Place order' }],
  placed: [
    { to: 'ready_for_pickup', label: 'Vendor says ready' },
    { to: 'delivered', label: 'Mark delivered' },
  ],
  ready_for_pickup: [{ to: 'picked_up', label: 'Mark picked up' }],
  picked_up: [],
  delivered: [],
  receipt_verified: [],
}

export function PurchaseOrders({ me }: { me: MeResponse }) {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const tab = params.get('tab') ?? 'open'
  const selectedId = params.get('po')

  const [data, setData] = useState<PurchaseOrderResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const canEdit = me.permissions.purchasing === 'edit' || me.permissions.purchasing === 'full'

  const load = useCallback(async () => {
    try {
      setData(await request<PurchaseOrderResponse>(`/purchasing/purchase-orders?tab=${tab}`))
      setError(null)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Orders could not load.')
    }
  }, [tab])

  useEffect(() => { void load() }, [load])

  const selected = useMemo(
    () => data?.items.find((row) => row.id === selectedId) ?? null, [data, selectedId])

  const byVendor = useMemo(() => {
    const groups = new Map<string, PurchaseOrderRow[]>()
    for (const row of data?.items ?? []) {
      groups.set(row.vendorId, [...(groups.get(row.vendorId) ?? []), row])
    }
    return [...groups.values()]
  }, [data])

  async function move(row: PurchaseOrderRow, to: string, label: string) {
    setBusy(true)
    try {
      await request(`/purchasing/purchase-orders/${row.id}/transition`, {
        method: 'POST', body: { to },
      })
      setNotice(`${row.reference} · ${label.toLowerCase()}.`)
      await load()
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : 'That could not be changed.')
    } finally {
      setBusy(false)
    }
  }

  const missingReceipts = data?.counts.awaiting_receipt ?? 0

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageBar
        title="Purchase orders"
        breadcrumbs={[{ label: 'Purchasing', onClick: () => navigate('/purchasing') }]}
        sub={`${me.scope.label} · grouped by vendor`}
        tabs={TABS.map((entry) => ({
          key: entry.key,
          label: entry.label,
          count: data?.counts[entry.key],
        }))}
        activeTab={tab}
        onTabChange={(key) => setParams({ tab: key })}
      />

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {error && (
          <EmptyState title="Orders could not load" body={error}
            action={{ label: 'Try again', onClick: () => void load() }} />
        )}

        {data && (
          <>
            <div className="mb-3 grid gap-2.5 sm:grid-cols-2 xl:grid-cols-4">
              <MetricCard
                label="Open" value={String(data.counts.open ?? 0)}
                sub="not yet synced or closed"
                dotColor="rgb(var(--color-info))"
                selected={tab === 'open'}
                onClick={() => setParams({ tab: 'open' })}
              />
              <MetricCard
                label="Pickups today" value={String(data.counts.pickups_today ?? 0)}
                sub="someone has to collect these"
                dotColor="rgb(var(--color-accent))"
                selected={tab === 'pickups_today'}
                onClick={() => setParams({ tab: 'pickups_today' })}
              />
              <MetricCard
                label="Receipt missing" value={String(missingReceipts)}
                sub={missingReceipts > 0
                  ? 'collected, nobody photographed the paperwork'
                  : 'every collection has its receipt'}
                dotColor="rgb(var(--color-danger))"
                selected={tab === 'awaiting_receipt'}
                onClick={() => setParams({ tab: 'awaiting_receipt' })}
              />
              <MetricCard
                label="Ready to sync" value={String(data.counts.ready_to_sync ?? 0)}
                sub="verified, not yet in QuickBooks"
                dotColor="rgb(var(--color-accent))"
                selected={tab === 'ready_to_sync'}
                onClick={() => setParams({ tab: 'ready_to_sync' })}
              />
            </div>

            <div className={cn('grid gap-4', selected && 'xl:grid-cols-[1fr_minmax(320px,400px)]')}>
              <div className="flex min-w-0 flex-col gap-3">
                {byVendor.length === 0 && (
                  <Card className="p-6">
                    <p className="m-0 text-body text-text-muted">
                      Nothing here. Orders are created from the Materials needed screen.
                    </p>
                  </Card>
                )}

                {byVendor.map((orders) => {
                  const vendor = orders[0]!
                  return (
                    <Card key={vendor.vendorId} className="min-w-0 p-0">
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 border-b border-line p-3">
                        <SectionLabel>{vendor.vendorName}</SectionLabel>
                        {vendor.vendorCity && (
                          <span className="text-cell text-text-muted">{vendor.vendorCity}</span>
                        )}
                        <span className="text-cell text-text-muted">
                          · {orders.length} {orders.length === 1 ? 'PO' : 'POs'}
                        </span>
                        {vendor.terms && (
                          <span className="text-cell text-text-muted">· {vendor.terms}</span>
                        )}
                        {vendor.counterHours && (
                          <span className="ml-auto text-cell text-text-faint">
                            {vendor.counterHours}
                          </span>
                        )}
                      </div>

                      {orders.map((row) => (
                        <button
                          key={row.id}
                          type="button"
                          onClick={() => setParams((next) => {
                            const updated = new URLSearchParams(next)
                            updated.set('po', row.id)
                            return updated
                          })}
                          className={cn(
                            'flex w-full flex-wrap items-center gap-3 border-b border-line-soft px-3 py-2.5 text-left last:border-b-0',
                            row.id === selectedId ? 'bg-accent-soft' : 'hover:bg-hover')}
                        >
                          <span className="min-w-[180px] flex-1">
                            <span className="block text-body font-bold text-text">
                              {row.reference}
                            </span>
                            <span className="block text-cell text-text-muted">
                              {orderSummary(row) ?? 'No lines yet'}
                              {row.forJobs && <> · {row.forJobs}</>}
                            </span>
                          </span>

                          <span className="shrink-0 text-cell text-text-muted">
                            {row.how === 'pickup' ? 'Pickup' : 'Delivery'}
                            {row.expectedOn && <> · {shortDate(row.expectedOn)}</>}
                            {row.windowNote && <> · {row.windowNote}</>}
                          </span>

                          {(row.assignedUserName ?? row.assignedCrewName) && (
                            <span className="flex shrink-0 items-center gap-1.5">
                              <Avatar
                                name={row.assignedUserName ?? row.assignedCrewName ?? ''}
                                size={24}
                              />
                              <span className="text-cell text-text-muted">
                                {row.assignedUserName ?? row.assignedCrewName}
                              </span>
                            </span>
                          )}

                          <Tag tone={statusTone(row)} className="shrink-0">
                            {STATUS_LABEL[row.status] ?? row.status}
                          </Tag>

                          {needsReceipt(row) && (
                            <Tag tone="bad" className="shrink-0">
                              Receipt missing
                              {row.collectedAt && <> · {formatRelative(row.collectedAt)}</>}
                            </Tag>
                          )}
                        </button>
                      ))}
                    </Card>
                  )
                })}
              </div>

              {selected && (
                <aside className="flex h-fit flex-col rounded-card border border-line bg-surface">
                  <header className="border-b border-line p-4">
                    <p className="m-0 flex flex-wrap items-center gap-2">
                      <span className="text-title">{selected.reference}</span>
                      <Tag tone={statusTone(selected)}>
                        {STATUS_LABEL[selected.status] ?? selected.status}
                      </Tag>
                    </p>
                    <p className="m-0 mt-1 text-body text-text-muted">
                      {selected.vendorName}
                      {selected.placedAt && <> · placed {formatRelative(selected.placedAt)}</>}
                      {selected.counterHours && <> · {selected.counterHours}</>}
                    </p>
                  </header>

                  <div className="flex flex-col gap-3 p-4">
                    <div>
                      <SectionLabel className="mb-1 block">Items</SectionLabel>
                      {boughtLines(selected).length > 0
                        ? <BoughtLines lines={boughtLines(selected)} />
                        : (
                          <p className="m-0 text-body text-text">
                            {selected.itemsSummary ?? 'No lines yet'}
                          </p>
                        )}
                      {selected.forJobs && (
                        <p className="m-0 mt-0.5 text-cell text-text-muted">
                          For {selected.forJobs}
                        </p>
                      )}
                      {boughtLines(selected).length > 0
                        ? (
                          /* Board 2: the order is in the vendor's unit and the demand is not. */
                          <p className="m-0 mt-1.5 text-cell text-text-muted">
                            Ordered in the unit the vendor sells. The demand list still counts{' '}
                            {buyingUnitWord(boughtLines(selected)[0]!.unit, undefined, 2)}.
                          </p>
                        )
                        : (
                          /* §A.1: the vendor's price is what values the stock, and
                             it does not exist until the receipt does. */
                          <p className="m-0 mt-1.5 text-cell text-text-faint">
                            Quantities only. The vendor’s price lands with the receipt.
                          </p>
                        )}
                    </div>

                    <div>
                      <SectionLabel className="mb-1 block">
                        {selected.how === 'pickup' ? 'Pickup' : 'Delivery'}
                      </SectionLabel>
                      <p className="m-0 text-body text-text">
                        {selected.assignedUserName ?? selected.assignedCrewName ?? 'Nobody assigned'}
                        {selected.expectedOn && <> · {shortDate(selected.expectedOn)}</>}
                        {selected.windowNote && <> · {selected.windowNote}</>}
                      </p>
                      <p className="m-0 mt-0.5 text-cell text-text-muted">
                        To {selected.yardName}
                        {selected.willCallRef && <> · will call {selected.willCallRef}</>}
                      </p>
                    </div>

                    <div>
                      <SectionLabel className="mb-1 block">Receipt &amp; verification</SectionLabel>
                      {selected.verifiedCount > 0
                        ? (
                          <p className="m-0 text-body text-accent-dark">
                            Verified. The stock is at {selected.yardName} and the yard’s
                            average moved to the price on the receipt.
                          </p>
                        )
                        : selected.receiptCount > 0
                          ? (
                            <p className="m-0 text-body text-text-muted">
                              Photographed, not yet verified. Ops matches the lines, flags
                              any price or quantity difference, and verifying is what posts
                              the stock.
                            </p>
                          )
                          : (
                            <p className="m-0 rounded-card border border-dashed border-line p-3 text-body text-text-muted">
                              Whoever collects it photographs the receipt at the counter.
                              Nothing reaches the yard’s stock until it is verified.
                            </p>
                          )}
                    </div>
                  </div>

                  <footer className="flex flex-wrap justify-end gap-2 border-t border-line p-4">
                    {canEdit && (NEXT[selected.status] ?? []).map((step) => (
                      <Button
                        key={step.to}
                        variant="primary"
                        disabled={busy}
                        onClick={() => void move(selected, step.to, step.label)}
                      >
                        {step.label}
                      </Button>
                    ))}
                    {(NEXT[selected.status] ?? []).length === 0 && (
                      <span className="text-cell text-text-muted">
                        {selected.status === 'picked_up' || selected.status === 'delivered'
                          ? 'Waiting on the receipt.'
                          : 'Nothing to do here.'}
                      </span>
                    )}
                  </footer>
                </aside>
              )}
            </div>
          </>
        )}
      </div>

      {notice && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2">
          <Toast onDismiss={() => setNotice(null)}>{notice}</Toast>
        </div>
      )}
    </div>
  )
}

/** Collected, and nobody has photographed the paperwork. */
const needsReceipt = (row: PurchaseOrderRow): boolean =>
  (row.status === 'picked_up' || row.status === 'delivered') && row.receiptCount === 0

function statusTone(row: PurchaseOrderRow): TagTone {
  if (needsReceipt(row)) return 'bad'
  if (row.status === 'ready_for_pickup' || row.status === 'synced') return 'ok'
  if (row.status === 'draft') return 'warn'
  return 'plain'
}

const shortDate = (iso: string): string =>
  parseDateOnly(iso)?.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC',
  }) ?? iso

/**
 * The row's one line. A PO ordered in the vendor's unit says so —
 * `Holland paver, Charcoal × 3 skids` — rather than the 1,560 the line holds,
 * because the skids are what somebody collects at the counter.
 */
function orderSummary(row: PurchaseOrderRow): string | null {
  if (boughtLines(row).length === 0) return row.itemsSummary
  return (row.lines ?? []).map((line) => line.buyQty !== null
    ? `${line.name} × ${buyingCount(Number(line.buyQty), line.buyUnitName ?? line.buyUnit ?? '')}`
    : `${line.name} ×${Number(line.qty)}`).join(' · ')
}

/** The lines ordered in the vendor's unit — the ones Board 2 draws with their sum. */
const boughtLines = (row: PurchaseOrderRow): PurchaseOrderLine[] =>
  (row.lines ?? []).filter((line) => line.buyQty !== null)

/**
 * Board 2's PO lines: the item and what the order comes to, then
 * `3 skids × $486.50 = 1,560 sq ft` — the vendor's unit, and the allocation
 * quantity it is. `poLineWords` is the one sentence.
 */
function BoughtLines({ lines }: { lines: PurchaseOrderLine[] }) {
  return (
    <ul className="m-0 flex list-none flex-col gap-2 p-0">
      {lines.map((line, index) => (
        <li key={index} data-po-line="" className="flex flex-col gap-0.5">
          <span className="flex items-baseline gap-2 text-body">
            <b className="min-w-0 flex-1 truncate font-semibold text-text">{line.name}</b>
            <b className="tnum shrink-0 font-bold text-text">
              {formatRate(Number(line.buyQty) * Number(line.buyPrice))}
            </b>
          </span>
          <span className="tnum text-cell text-text-muted">
            {poLineWords(Number(line.buyQty), line.buyUnitName ?? line.buyUnit ?? '',
              Number(line.buyPrice), Number(line.qty), line.unit)}
          </span>
        </li>
      ))}
    </ul>
  )
}
